import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import { useLoyaltyRewards, useSaveReward, useDeleteReward, useReorderRewards } from '../../queries/loyalty';
import LoyaltyNav from './LoyaltyNav';
import '../../styles/honey-ink.css';
import { useInHub } from '../../nav/useInHub';

const EMPTY = { name: '', name_ar: '', points_required: 50, type: 'free_item', value: '', image_url: '', active: true };
const ICON = { free_item: '☕', discount: '🏷️', free_spin: '🎡' };

export default function LoyaltyRewards() {
  const { t } = useTranslation();
  const inHub = useInHub();
  const { data, isLoading } = useLoyaltyRewards();
  const save = useSaveReward();
  const del = useDeleteReward();
  const reorder = useReorderRewards();

  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(null);
  const dragFrom = useRef(null);
  const [overIdx, setOverIdx] = useState(null);

  useEffect(() => { if (Array.isArray(data)) setRows(data); }, [data]);

  const typeOptions = [
    { label: `☕ ${t('loyalty.type_free_item')}`, value: 'free_item' },
    { label: `🏷️ ${t('loyalty.type_discount')}`, value: 'discount' },
    { label: `🎡 ${t('loyalty.type_free_spin')}`, value: 'free_spin' },
  ];
  const typeLabel = (v) => (typeOptions.find((o) => o.value === v)?.label ?? v);

  const onDrop = (to) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    setOverIdx(null);
    if (from == null || from === to) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
    reorder.mutate(next.map((r) => r.id));
  };

  const confirmDelete = (row) =>
    confirmDialog({
      message: t('loyalty.delete_reward_confirm', { name: row.name }),
      header: t('loyalty.delete'),
      icon: 'pi pi-exclamation-triangle',
      className: 'lty-app',
      acceptClassName: 'p-button-danger',
      accept: () => del.mutate(row.id),
    });

  const setD = (k, v) => setDialog((d) => ({ ...d, [k]: v }));
  const valid = dialog && dialog.name?.trim() && dialog.points_required > 0 && dialog.type;
  const submit = () => save.mutate(dialog, { onSuccess: () => setDialog(null) });

  return (
    <div className="lty-app lty-page p-4 md:p-6">
      <ConfirmDialog />
      <LoyaltyNav />

      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        {!inHub ? (
        <div className="flex items-start gap-3">
          <div className="lty-head-icon"><i className="pi pi-gift" /></div>
          <div>
            <div className="lty-title">{t('loyalty.rewards_title')}</div>
            <div className="lty-sub">{t('loyalty.rewards_sub')}</div>
          </div>
        </div>
        ) : <span />}
        <Button label={t('loyalty.add_reward')} icon="pi pi-plus" onClick={() => setDialog({ ...EMPTY })} />
      </div>

      <div className="lty-onboard"><span className="ic">💡</span><span className="tx">{t('loyalty.onboard_rewards')}</span></div>

      {isLoading ? (
        <div className="lty-reward-list">{[0, 1, 2].map((i) => <Skeleton key={i} height="76px" />)}</div>
      ) : !rows.length ? (
        <div className="lty-empty">{t('loyalty.no_rewards')}</div>
      ) : (
        <div className="lty-reward-list">
          {rows.map((r, idx) => (
            <div
              key={r.id}
              className={`lty-reward-card${overIdx === idx ? ' drag-over' : ''}`}
              draggable
              onDragStart={() => { dragFrom.current = idx; }}
              onDragOver={(e) => { e.preventDefault(); if (overIdx !== idx) setOverIdx(idx); }}
              onDragLeave={() => setOverIdx((o) => (o === idx ? null : o))}
              onDrop={(e) => { e.preventDefault(); onDrop(idx); }}
            >
              <span className="lty-drag" aria-hidden="true">⠿</span>
              <span className="lty-reward-icon">{ICON[r.type] || '🎁'}</span>
              <div className="lty-reward-main">
                <div className="lty-reward-name">{r.name}</div>
                {r.name_ar && <div className="lty-reward-ar" dir="rtl">{r.name_ar}</div>}
                <div className="lty-reward-meta">
                  <span>{typeLabel(r.type)}</span>
                  <span className={`lty-pill ${r.active ? 'on' : 'off'}`}>{r.active ? t('loyalty.on') : t('loyalty.off')}</span>
                </div>
              </div>
              <div className="lty-reward-points">{r.points_required}<small>{t('loyalty.points')}</small></div>
              <div className="lty-reward-actions">
                <Button icon="pi pi-pencil" rounded text onClick={() => setDialog({ ...r })} aria-label="edit" />
                <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => confirmDelete(r)} aria-label="delete" />
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog visible={!!dialog} onHide={() => setDialog(null)} style={{ width: '30rem', maxWidth: '95vw' }}
        className="lty-app" header={dialog?.id ? t('loyalty.edit_reward') : t('loyalty.add_reward')} modal
        footer={
          <div className="flex justify-end gap-2">
            <Button label={t('loyalty.cancel')} text onClick={() => setDialog(null)} />
            <Button label={t('loyalty.save')} icon="pi pi-check" disabled={!valid} loading={save.isLoading} onClick={submit} />
          </div>
        }>
        {dialog && (
          <div className="flex flex-col gap-4 pt-2">
            <Row label={t('loyalty.name_en')}>
              <InputText value={dialog.name} onChange={(e) => setD('name', e.target.value)} className="w-full" />
            </Row>
            <Row label={t('loyalty.name_ar')}>
              <InputText value={dialog.name_ar || ''} onChange={(e) => setD('name_ar', e.target.value)} className="w-full" dir="rtl" />
            </Row>
            <div className="grid grid-cols-2 gap-3">
              <Row label={t('loyalty.points_required')}>
                <InputNumber value={dialog.points_required} min={1} max={1000000} className="w-full"
                  onValueChange={(e) => setD('points_required', e.value ?? 0)} />
              </Row>
              <Row label={t('loyalty.type')}>
                <Dropdown value={dialog.type} options={typeOptions} onChange={(e) => setD('type', e.value)} className="w-full" panelClassName="lty-app" />
              </Row>
            </div>
            <Row label={t('loyalty.value')} hint={t('loyalty.value_hint')}>
              <InputText value={dialog.value || ''} onChange={(e) => setD('value', e.target.value)} className="w-full" />
            </Row>
            <Row label={t('loyalty.image_url')} hint={t('loyalty.image_url_hint')}>
              <InputText value={dialog.image_url || ''} onChange={(e) => setD('image_url', e.target.value)} className="w-full" />
            </Row>
            <div className="flex items-center gap-2">
              <InputSwitch checked={!!dialog.active} onChange={(e) => setD('active', e.value)} />
              <span>{t('loyalty.active')}</span>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div>
      <label className="lty-field-label">{label}</label>
      {hint && <div className="lty-hint">{hint}</div>}
      {children}
    </div>
  );
}
